import { Chip } from "@mui/material";
import {
  CheckCircle, ErrorOutlined, HourglassEmpty, ReportProblem,
} from "@mui/icons-material";

/** Compliance levels rolled up across a collection (investigators or
 * locations). Used by the panel headers to nudge the operator when
 * any underlying row needs attention. */
export type RollupLevel = "ok" | "warning" | "critical" | "pending";

const VARIANT: Record<RollupLevel, {
  color: "success" | "warning" | "error" | "default";
  icon: typeof CheckCircle;
  label: string;
}> = {
  ok: { color: "success", icon: CheckCircle, label: "On track" },
  warning: { color: "warning", icon: ReportProblem, label: "Expiring" },
  critical: { color: "error", icon: ErrorOutlined, label: "Action needed" },
  pending: { color: "default", icon: HourglassEmpty, label: "Pending" },
};

/**
 * Small status chip for panel headers. Carries an icon AND a word so
 * the meaning is legible without color (accessibility for colorblind
 * users + general scanability).
 */
export function RollupStatusChip({ level }: { level: RollupLevel }) {
  const v = VARIANT[level];
  const Icon = v.icon;
  return (
    <Chip
      size="small"
      label={v.label}
      color={v.color}
      icon={<Icon fontSize="small" />}
      sx={{ ml: 0.5 }}
    />
  );
}

/** Reduce a list of per-document statuses to a single rollup level.
 * Critical wins over warning, which wins over pending; everything
 * current returns ``ok``. Empty list returns ``pending`` since we
 * can't claim anything is in order. */
export function rollupDocStatuses(
  statuses: ("current" | "expiring" | "expired" | "missing" | "in_progress")[],
): RollupLevel {
  if (statuses.length === 0) return "pending";
  if (statuses.some((s) => s === "missing" || s === "expired")) return "critical";
  if (statuses.some((s) => s === "expiring")) return "warning";
  if (statuses.some((s) => s === "in_progress")) return "pending";
  return "ok";
}

/** Reduce a list of milestone-style colored statuses to a single
 * rollup level. Used by the locations panel where each location
 * carries an ``artifact_rollup`` of these colors. */
export function rollupMilestoneStatuses(
  colors: ("green" | "yellow" | "red" | "gray")[],
): RollupLevel {
  if (colors.length === 0) return "pending";
  if (colors.includes("red")) return "critical";
  if (colors.includes("yellow")) return "warning";
  if (colors.includes("gray")) return "pending";
  return "ok";
}
